/**
 * @voltxampere/web — `<vxa-erc-badge>` (Sprint 56c)
 *
 * Topbar chip that surfaces the current ERC violation count. Hidden
 * (size 0) when no violations are outstanding; coloured by the
 * dominant severity when 1+ visible:
 *   - any `error`  → red (var(--danger))
 *   - else `warn`  → brass (var(--brass-hot))
 *
 * Clicking the chip toggles the violations panel (`ercPanelOpen`
 * signal). Keyboard parity: Enter / Space activate the click handler.
 *
 * Re-renders reactively on `ercReport` AND `ignoredViolationKeys`
 * (the count is post-ignore-filter).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html } from "lit";
import { customElement } from "lit/decorators.js";

import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import {
  ercReport,
  ignoredViolationKeys,
  toggleErcPanel,
  visibleViolationCounts,
} from "../store/erc.js";

@customElement("vxa-erc-badge")
export class VxaErcBadge extends LitElement {
  private readonly _report = new SignalController(this, ercReport);
  // Ignore-set changes also alter the visible count, so subscribe.
  private readonly _ignored = new SignalController(this, ignoredViolationKeys);

  static override styles = css`
    :host {
      display: inline-flex;
    }

    button {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      text-transform: uppercase;
      padding: 4px 10px;
      cursor: pointer;
      background: transparent;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover),
        color var(--transition-hover);
    }

    button[data-severity="error"] {
      color: var(--danger, #d9534f);
      border: 1px solid var(--danger, #d9534f);
    }

    button[data-severity="error"]:hover {
      background: color-mix(in srgb, var(--danger, #d9534f) 12%, transparent);
    }

    button[data-severity="warn"] {
      color: var(--brass-hot, #d4a857);
      border: 1px solid var(--brass-faint);
    }

    button[data-severity="warn"]:hover {
      background: var(--brass-ghost);
      border-color: var(--brass-hot, #d4a857);
    }

    button:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: 2px;
    }

    .count {
      font-variant-numeric: tabular-nums;
    }

    .dot {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 8px;
      vertical-align: middle;
      background: currentColor;
    }

    /* Zero-state: chip hidden completely. Keeping :host visible
       would shift the surrounding chip layout each time a violation
       toggles. */
    :host([data-empty]) {
      display: none;
    }
  `;

  override render() {
    const counts = visibleViolationCounts(this._report.value);
    const total = counts.error + counts.warn + counts.info;

    if (total === 0) {
      // Reflect host attribute so the :host([data-empty]) style hides
      // the chip without a parent layout shift.
      this.toggleAttribute("data-empty", true);
      return html``;
    }
    this.toggleAttribute("data-empty", false);

    const severity = counts.error > 0 ? "error" : "warn";
    const label =
      counts.error > 0 && counts.warn > 0
        ? t("erc.badge.mixed", {
            errors: String(counts.error),
            warns: String(counts.warn),
          })
        : counts.error > 0
          ? t("erc.badge.summary.error", { count: String(counts.error) })
          : t("erc.badge.summary.warn", { count: String(counts.warn) });

    return html`
      <button
        type="button"
        data-severity=${severity}
        data-testid="erc-badge"
        title=${t("erc.badge.title")}
        aria-label=${label}
        @click=${this.onClick}
      >
        <span class="dot" aria-hidden="true"></span>
        <span class="count">${label}</span>
      </button>
    `;
  }

  private readonly onClick = (): void => {
    toggleErcPanel();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-erc-badge": VxaErcBadge;
  }
}
